import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Slider } from '../ui/slider';
import { Loader2, DollarSign, TrendingUp, Percent } from 'lucide-react';
import API_CONSTRUCTOR from '../../models/api-model';
import {
  formatCurrency,
  formatPercentage,
  calculateInvestmentAmount,
  calculateProfitShare,
  calculateExpectedReturn,
  calculateROI,
  getAvailablePercentageOptions,
  isValidCommitmentPercentage,
} from '../../utils/investorUtils';
import { toast } from 'sonner';

export const CommitmentDialog = ({ open, onClose, opportunity, onSuccess }) => {
  const [commitmentPercentage, setCommitmentPercentage] = useState(5);
  const [investorNotes, setInvestorNotes] = useState('');
  const [investorQuestions, setInvestorQuestions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Calculate investment details based on percentage
  const investmentAmount = calculateInvestmentAmount(opportunity?.total_investment_base || 0, commitmentPercentage);
  const profitShare = calculateProfitShare(opportunity?.investor_profit_total || 0, commitmentPercentage);
  const guaranteedReturnPct = opportunity?.return_rate || 15;
  const guaranteedReturnAmount = (investmentAmount * guaranteedReturnPct) / 100;
  const expectedReturn = calculateExpectedReturn(investmentAmount, profitShare, guaranteedReturnPct);
  const roi = calculateROI(investmentAmount, expectedReturn);

  // Get available percentage options
  const availablePercentages = getAvailablePercentageOptions(
    opportunity?.remaining_percentage_available || 100
  );

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setCommitmentPercentage(5);
      setInvestorNotes('');
      setInvestorQuestions('');
    }
  }, [open]);

  // Handle commitment submission
  const handleSubmit = async () => {
    if (!opportunity) return;

    // Validate that calculation exists
    if (!opportunity.calculation_id) {
      toast.error('Calculation not found for this property');
      return;
    }

    // Validate percentage
    if (!isValidCommitmentPercentage(commitmentPercentage)) {
      toast.error('Please select a valid commitment percentage (5% increments)');
      return;
    }

    // Validate against remaining percentage
    const remainingPercentage = opportunity.remaining_percentage_available || 100;
    if (commitmentPercentage > remainingPercentage) {
      toast.error(`Only ${formatPercentage(remainingPercentage)} remaining. Please choose a lower percentage.`);
      return;
    }

    try {
      setSubmitting(true);

      const api = API_CONSTRUCTOR();
      await api.post('/commitments', {
        queryId: opportunity.id,
        calculationId: opportunity.calculation_id,
        commitmentPercentage,
        investorNotes,
        investorQuestions,
      });

      toast.success('Commitment submitted successfully!');
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error submitting commitment:', err);
      const errorMessage = err.response?.data?.error || 'Failed to submit commitment';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!opportunity) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Commit to Investment</DialogTitle>
          <DialogDescription>
            Choose your investment percentage and provide any notes or questions.
            Commitments are subject to admin approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Investment Percentage Slider */}
          <div className="space-y-3">
            <Label>Investment Percentage</Label>
            <div className="flex items-center gap-4">
              <Slider
                value={[commitmentPercentage]}
                onValueChange={(value) => setCommitmentPercentage(value[0])}
                min={5}
                max={Math.min(100, opportunity.remaining_percentage_available || 100)}
                step={5}
                className="flex-1"
              />
              <span className="text-lg font-semibold min-w-[60px] text-right">
                {formatPercentage(commitmentPercentage, 0)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Minimum 5%, Maximum {formatPercentage(opportunity.remaining_percentage_available || 100, 0)} available
            </p>
          </div>

          {/* Investment Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-sm text-gray-700">Your Investment Summary</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Investment Amount</p>
                <p className="text-lg font-bold text-primary">{formatCurrency(investmentAmount)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Your Profit Share</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(profitShare)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-3 border-t">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Guaranteed Return ({guaranteedReturnPct}%)</p>
                <p className="text-sm font-semibold">{formatCurrency(guaranteedReturnAmount)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Expected Return</p>
                <p className="text-sm font-semibold text-blue-600">{formatCurrency(expectedReturn)}</p>
              </div>
            </div>

            <div className="pt-3 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Expected ROI</span>
                <span className="text-lg font-bold text-green-600">{formatPercentage(roi, 1)}</span>
              </div>
            </div>
          </div>

          {/* Investment Period */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              <strong>Investment Period:</strong> {opportunity.rental_period} months
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="investor-notes">Your Notes (Optional)</Label>
            <Textarea
              id="investor-notes"
              placeholder="Any additional notes or comments about your investment..."
              value={investorNotes}
              onChange={(e) => setInvestorNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Questions */}
          <div className="space-y-2">
            <Label htmlFor="investor-questions">Questions for Admin (Optional)</Label>
            <Textarea
              id="investor-questions"
              placeholder="Any questions you have about this investment..."
              value={investorQuestions}
              onChange={(e) => setInvestorQuestions(e.target.value)}
              rows={3}
            />
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              <strong>Important:</strong> This commitment is pending admin approval. 
              You will be notified once your commitment has been reviewed. 
              The admin may approve, reject, or request more information.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Submit Commitment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CommitmentDialog;
